<script setup lang="ts">
import {Icon} from '@iconify/vue'
import {computed, ref} from 'vue'

import type {BassButton, BassLayoutSettings} from '@/generated'
import {useSettingsStore} from '@/stores/settings'

import {BASS_LAYOUT_PRESETS, cloneBassLayout} from './instrumentData'
import BassBoard, {type BassButtonPosition} from './BassBoard.vue'
import BassLayoutPrintView from './BassLayoutPrintView.vue'

/**
 * Editor und Vorschau der Bass-/Begleitseite (Harmonika-Tab, Unter-Tab „Bass-Belegung").
 *
 * Knopfzahl und vor allem die Belegung der inneren Reihe sind bauartabhängig – deshalb ist das
 * Layout Konfiguration statt Tabelle im Code. Die Engine leitet daraus die Bass-Zeile ab
 * (`griffschrift/mapping/bass_layout.py`); der Wert reist über `ConversionSettings.BassLayout`
 * im Convert-Request mit.
 *
 * Bewusst KEINE Wiederverwendung von `InstrumentBody`: dessen Knöpfe tragen Tonlisten samt
 * vorberechneter MIDI-Werte für den Simulator, die Bass-Seite dagegen einen Griffschrift-Namen und
 * Akkord-Figures. Die Vorschau ist deshalb ein eigenes Raster (`BassBoard.vue`), das auch der
 * Akkord-Finder (Tab „Akkorde") zum Hervorheben der Bassknöpfe nutzt.
 */

// Gelesen wird das Layout aus dem Store, geschrieben über dessen Actions – Pinia gibt Computeds
// eines Setup-Stores nur lesend heraus.
const settingsStore = useSettingsStore()
const {setBassLayout, resetBassLayout} = settingsStore
const bassLayout = computed<BassLayoutSettings>(() => settingsStore.bassLayout)

/** Balgrichtung NUR der Vorschau – sie ändert die Konfiguration nicht. */
const direction = ref<'druck' | 'zug'>('druck')
const selected = ref<BassButtonPosition | null>(null)
const printPreviewOpen = ref(false)

/**
 * Jede Änderung ersetzt das gesamte Layout-Objekt. Das ist die Granularität, in der der Store es
 * persistiert und in der es zur Engine reist – eine In-place-Mutation würde `useLocalStorage`
 * nicht zuverlässig auslösen.
 */
function applyLayout(mutate: (draft: BassLayoutSettings) => void) {
  const draft = cloneBassLayout(bassLayout.value)
  mutate(draft)
  setBassLayout(draft)
}

function selectPreset(name: string) {
  const preset = BASS_LAYOUT_PRESETS.find((candidate) => candidate.name === name)
  if (!preset) return
  setBassLayout(cloneBassLayout(preset))
  selected.value = null
}

function updateButton(rowIndex: number, buttonIndex: number, patch: Partial<BassButton>) {
  applyLayout((draft) => {
    const button = draft.rows?.[rowIndex]?.buttons?.[buttonIndex]
    if (button) Object.assign(button, patch)
  })
}

function addButton(rowIndex: number) {
  applyLayout((draft) => {
    draft.rows?.[rowIndex]?.buttons?.push({griffschrift: '?', kind: 'bass' as BassButton['kind'], druck: null, zug: null})
  })
}

function removeButton(rowIndex: number, buttonIndex: number) {
  applyLayout((draft) => {
    draft.rows?.[rowIndex]?.buttons?.splice(buttonIndex, 1)
  })
  selected.value = null
}

function addRow() {
  applyLayout((draft) => {
    draft.rows = [...(draft.rows ?? []), {name: `Reihe ${(draft.rows?.length ?? 0) + 1}`, buttons: []}]
  })
}

function removeRow(rowIndex: number) {
  applyLayout((draft) => {
    draft.rows?.splice(rowIndex, 1)
  })
  selected.value = null
}

function reset() {
  resetBassLayout()
  selected.value = null
}

function isSelected(rowIndex: number, buttonIndex: number): boolean {
  return selected.value?.row === rowIndex && selected.value?.button === buttonIndex
}

/** Reihen der Tabelle in derselben Reihenfolge wie `BassBoard` – warum nicht-mutierend: siehe dort. */
const displayRows = computed(() =>
    (bassLayout.value.rows ?? []).map((row, index) => ({row, index})).reverse()
)
</script>

<template>
  <div class="flex flex-col gap-4">
  <header class="flex flex-wrap items-center gap-3">
    <label class="flex items-center gap-2 text-sm">
      <span class="font-medium text-gray-700">Bauart</span>
      <select
          class="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm"
          :value="bassLayout.name"
          @change="selectPreset(($event.target as HTMLSelectElement).value)"
      >
        <option v-for="preset in BASS_LAYOUT_PRESETS" :key="preset.name" :value="preset.name">
          {{ preset.name }}
        </option>
        <option v-if="!BASS_LAYOUT_PRESETS.some((p) => p.name === bassLayout.name)" :value="bassLayout.name">
          {{ bassLayout.name }}
        </option>
      </select>
    </label>

    <div class="flex items-center gap-1 rounded-md border border-gray-300 p-0.5 text-sm">
      <button
          type="button"
          class="rounded px-2 py-1 transition-colors"
          :class="direction === 'druck' ? 'bg-sky-600 text-white' : 'text-gray-600 hover:bg-gray-100'"
          @click="direction = 'druck'"
      >
        Druck
      </button>
      <button
          type="button"
          class="rounded px-2 py-1 transition-colors"
          :class="direction === 'zug' ? 'bg-sky-600 text-white' : 'text-gray-600 hover:bg-gray-100'"
          @click="direction = 'zug'"
      >
        Zug
      </button>
    </div>

    <div class="ml-auto flex items-center gap-2">
      <button type="button" class="btn btn-ghost !px-2 !py-1 text-sm" @click="printPreviewOpen = true">
        <Icon icon="lucide:printer" class="mr-1"/>
        Druckansicht
      </button>
      <button type="button" class="btn btn-ghost !px-2 !py-1 text-sm" @click="addRow">
        <Icon icon="lucide:rows-3" class="mr-1"/>
        Reihe
      </button>
      <button type="button" class="btn btn-ghost !px-2 !py-1 text-sm" @click="reset">
        <Icon icon="lucide:rotate-ccw" class="mr-1"/>
        Zurücksetzen
      </button>
    </div>
  </header>

  <!-- Vorschau: die Knöpfe in Reihenfolge, beschriftet mit Griffschrift-Name und dem, was sie
       in der gewählten Balgrichtung erzeugen. Klick wählt die Zeile in der Tabelle. -->
  <BassBoard
      v-model:selected="selected"
      :layout="bassLayout"
      :direction="direction"
      editable
      @add-button="addButton"
  />

  <!-- Tabelle: die eigentliche Konfiguration. -->
  <div class="overflow-x-auto">
  <table class="w-full min-w-max text-left text-sm">
  <thead class="border-b border-gray-200 text-xs uppercase text-gray-500">
    <tr>
      <th class="px-2 py-1">Reihe</th>
      <th class="px-2 py-1">Pos</th>
      <th class="px-2 py-1">Griffschrift</th>
      <th class="px-2 py-1">Typ</th>
      <th class="px-2 py-1">Druck</th>
      <th class="px-2 py-1">Zug</th>
      <th class="px-2 py-1"></th>
    </tr>
  </thead>
  <tbody>
    <template v-for="{row, index: rowIndex} in displayRows" :key="rowIndex">
      <tr
          v-for="(button, buttonIndex) in row.buttons ?? []"
          :key="`${rowIndex}-${buttonIndex}`"
          class="border-b border-gray-100"
          :class="isSelected(rowIndex, buttonIndex) ? 'bg-sky-50' : ''"
          @click="selected = {row: rowIndex, button: buttonIndex}"
      >
        <td class="px-2 py-1 text-gray-500">{{ row.name }}</td>
        <td class="px-2 py-1 text-gray-400">{{ buttonIndex + 1 }}</td>
        <td class="px-2 py-1">
          <input
              class="w-20 rounded border border-gray-300 px-1 py-0.5"
              :value="button.griffschrift"
              maxlength="4"
              @change="updateButton(rowIndex, buttonIndex, {griffschrift: ($event.target as HTMLInputElement).value})"
          >
        </td>
        <td class="px-2 py-1">
          <select
              class="rounded border border-gray-300 px-1 py-0.5"
              :value="button.kind"
              @change="updateButton(rowIndex, buttonIndex, {kind: ($event.target as HTMLSelectElement).value as BassButton['kind']})"
          >
            <option value="bass">Grundbass</option>
            <option value="chord">Akkord</option>
          </select>
        </td>
        <td class="px-2 py-1">
          <input
              class="w-24 rounded border border-gray-300 px-1 py-0.5"
              :value="button.druck ?? ''"
              :placeholder="button.kind === 'chord' ? 'z. B. C' : 'z. B. G'"
              @change="updateButton(rowIndex, buttonIndex, {druck: ($event.target as HTMLInputElement).value || null})"
          >
        </td>
        <td class="px-2 py-1">
          <input
              class="w-24 rounded border border-gray-300 px-1 py-0.5"
              :value="button.zug ?? ''"
              :placeholder="button.kind === 'chord' ? 'z. B. G7' : 'z. B. D'"
              @change="updateButton(rowIndex, buttonIndex, {zug: ($event.target as HTMLInputElement).value || null})"
          >
        </td>
        <td class="px-2 py-1">
          <button
              type="button"
              class="text-gray-400 transition-colors hover:text-red-600"
              :aria-label="`Knopf ${button.griffschrift} entfernen`"
              @click.stop="removeButton(rowIndex, buttonIndex)"
          >
            <Icon icon="lucide:trash-2"/>
          </button>
        </td>
      </tr>
      <tr class="border-b border-gray-200">
        <td colspan="7" class="px-2 py-1">
          <button type="button" class="text-xs text-sky-700 hover:underline" @click="addButton(rowIndex)">
            + Knopf in „{{ row.name }}“
          </button>
          <button type="button" class="ml-3 text-xs text-gray-400 hover:text-red-600" @click="removeRow(rowIndex)">
            Reihe entfernen
          </button>
        </td>
      </tr>
    </template>
  </tbody>
  </table>
  </div>

  <p class="text-xs text-gray-500">
    Tonnamen in music21-Schreibweise (<code>B-</code> = b, <code>B</code> = h); deutsche Eingaben
    wie <code>H</code>, <code>Es</code> oder <code>Fis</code> werden ebenfalls erkannt.
    Akkorde als Figure, z. B. <code>C</code>, <code>G7</code>, <code>Dm</code>. Ein leeres Feld
    heißt: dieser Knopf klingt in der Richtung nicht.
  </p>
  </div>
  <BassLayoutPrintView v-if="printPreviewOpen" :layout="bassLayout" @close="printPreviewOpen = false"/>
</template>
